import * as THREE from 'three';
import { makeNormalTexture } from './textures';
import { WEAPONS, type WeaponId } from '../game/weapons';
import { preloadGLB, cloneGLBIfReady, eachVertexLocal, fitGLBHeld } from './gltf';
import { buildableScale } from './resizers';
import { weaponFit } from './weaponFit';

// Held weapon models (first-person viewmodel + third-person hand). SCRAPPY / SCAVENGED aesthetic: cobbled from
// salvaged steel pipe, bolted scrap plates, rivets, tape-wrapped grips and mismatched wood -- like they were built
// from what you find in barrels. Metal is real MeshStandardMaterial (metalness + roughness + a scratched normal map),
// so it reflects the scene's RoomEnvironment and reads as SHINY, dented steel; barrels/round parts use many-segment
// cylinders so they're actually round. Authored in the FIRST-PERSON frame (origin at the grip, weapon along -Z):
// two-handed long guns sit ~y0.5, one-handed ~y0..0.3. buildWeapon3p() re-seats the same model into the 3P hand.
//
// disposeChildren() (viewmodel) disposes a swapped model's MATERIALS, so every material is built PER MODEL here (the
// shared normal-map TEXTURE survives dispose -- material.dispose() doesn't free textures). Cheap: swaps are rare.

const METAL_N = makeNormalTexture({ size: 256, octaves: 5, period: 8, strength: 1.7, seed: 41 });
METAL_N.repeat.set(2, 2);

// --- scavenged materials (fresh per build; share the normal texture) ---
const std = (c: number, rough: number, metal: number, flat = false): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({ color: c, roughness: rough, metalness: metal, normalMap: METAL_N, normalScale: new THREE.Vector2(0.6, 0.6), flatShading: flat });
const STEEL = (c = 0x94908a): THREE.MeshStandardMaterial => std(c, 0.52, 0.4);    // scavenged steel: warm-neutral gray, moderate sheen (not a blue sky-mirror)
const GUNMETAL = (): THREE.MeshStandardMaterial => std(0x3a3733, 0.55, 0.4);      // warm dark gunmetal
const SCRAP = (c = 0x6b6f76): THREE.MeshStandardMaterial => std(c, 0.82, 0.6, true); // rough bolted-on plate
const RUST = (c = 0x8a5230): THREE.MeshStandardMaterial => std(c, 0.86, 0.35, true); // rusted accent
const WOOD = (c = 0x6b4a2b): THREE.MeshStandardMaterial => new THREE.MeshStandardMaterial({ color: c, roughness: 0.86, metalness: 0.04, flatShading: true });
const TAPE = (): THREE.MeshStandardMaterial => new THREE.MeshStandardMaterial({ color: 0x24242a, roughness: 0.96, metalness: 0.0, flatShading: true }); // rubber/cloth wrap

// --- primitive helpers ---
function box(g: THREE.Group, w: number, h: number, d: number, x: number, y: number, z: number, m: THREE.Material, rx = 0, ry = 0): THREE.Mesh {
  const e = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m); e.position.set(x, y, z); e.rotation.set(rx, ry, 0); g.add(e); return e;
}
// A round tube along an axis (barrels/shafts). 'z' = forward, 'y' = upright, 'x' = sideways. Many segments = round.
function rod(g: THREE.Group, r: number, len: number, x: number, y: number, z: number, m: THREE.Material, axis: 'z' | 'y' | 'x' = 'z', seg = 14, r2 = r): THREE.Mesh {
  const e = new THREE.Mesh(new THREE.CylinderGeometry(r, r2, len, seg), m);
  if (axis === 'z') e.rotation.x = Math.PI / 2; else if (axis === 'x') e.rotation.z = Math.PI / 2;
  e.position.set(x, y, z); g.add(e); return e;
}
// A raised band / tape wrap around a tube (torus lying in the XY plane by default, facing +Z).
function band(g: THREE.Group, r: number, thick: number, x: number, y: number, z: number, m: THREE.Material): void {
  const e = new THREE.Mesh(new THREE.TorusGeometry(r, thick, 6, 14), m); e.position.set(x, y, z); g.add(e);
}
function rivet(g: THREE.Group, x: number, y: number, z: number): void {
  const e = new THREE.Mesh(new THREE.SphereGeometry(0.008, 6, 4), GUNMETAL()); e.position.set(x, y, z); g.add(e);
}
// A bolted scrap plate (front at +/- along its thin axis) with corner rivets -- the signature "welded-on salvage".
function plate(g: THREE.Group, w: number, h: number, x: number, y: number, z: number, m: THREE.Material, tilt = 0): void {
  box(g, w, h, 0.012, x, y, z, m, 0, 0).rotation.z = tilt;
  for (const sx of [-1, 1]) for (const sy of [-1, 1]) rivet(g, x + sx * (w / 2 - 0.012), y + sy * (h / 2 - 0.012), z + 0.008);
}
// A tape/cloth-wrapped grip: a wood/steel core with a few dark wrap rings.
function grip(g: THREE.Group, w: number, h: number, d: number, x: number, y: number, z: number, rx: number): void {
  box(g, w, h, d, x, y, z, WOOD(0x5a3d22), rx);
  for (const o of [-h * 0.3, 0, h * 0.3]) { const t = new THREE.Mesh(new THREE.BoxGeometry(w * 1.15, h * 0.16, d * 1.15), TAPE()); t.position.set(x - Math.sin(rx) * o * 0, y + o, z); t.rotation.x = rx; g.add(t); }
}
// A trigger guard: a thin loop in the gun's vertical side-plane, hanging under the receiver, with a trigger blade.
function triggerGuard(g: THREE.Group, x: number, y: number, z: number): void {
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.026, 0.006, 6, 14), GUNMETAL()); ring.position.set(x, y - 0.02, z); ring.rotation.y = Math.PI / 2; g.add(ring);
  box(g, 0.008, 0.03, 0.01, x, y - 0.008, z + 0.006, GUNMETAL(), -0.3); // trigger blade
}
// A perforated barrel shroud: a boxed body with a row of drilled holes on each side (the makeshift-SMG look).
function shroud(g: THREE.Group, w: number, h: number, len: number, x: number, y: number, z: number, m: THREE.Material): void {
  box(g, w, h, len, x, y, z, m);
  for (let i = 0; i < 4; i++) { const hz = z - len / 2 + 0.05 + i * (len / 5); const hole = new THREE.Mesh(new THREE.CylinderGeometry(0.011, 0.011, w + 0.012, 8), GUNMETAL()); hole.rotation.z = Math.PI / 2; hole.position.set(x, y, hz); g.add(hole); }
}

// --- MODELING.md technique helpers (round -> lathe, flat -> extrude WITH bevel, bent -> tube) --------

// LATHE a revolved part from a [radius, along] profile. `along` runs toward -Z (the muzzle) when
// axis='z' (the default: barrels, brakes, scope tubes) or +Y when axis='y' (vertical parts).
// z is where along=0 sits. Profile radii > 0; 16+ segments so round parts are actually round.
function lathe(g: THREE.Group, profile: Array<[number, number]>, x: number, y: number, z: number, m: THREE.Material, seg = 16, axis: 'z' | 'y' = 'z'): THREE.Mesh {
  const pts = profile.map(([r, a]) => new THREE.Vector2(Math.max(r, 0.0004), a));
  const geo = new THREE.LatheGeometry(pts, seg);
  if (axis === 'z') geo.rotateX(-Math.PI / 2); // profile's +Y -> -Z (muzzle-ward)
  const e = new THREE.Mesh(geo, m);
  e.position.set(x, y, z);
  g.add(e);
  return e;
}
// EXTRUDE a 2D outline with the bevel FORCED ON (the one raw ExtrudeGeometry call in this file --
// every flat part routes through here so no sharp CGI-fake edges exist). The shape is drawn in the
// gun's SIDE plane (x = along barrel toward -Z, y = up); depth runs across the gun (thickness).
function extr(g: THREE.Group, shape: THREE.Shape, depth: number, x: number, y: number, z: number, m: THREE.Material, o?: { bevel?: number; seg?: number }): THREE.Mesh {
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth, bevelEnabled: true, // NON-NEGOTIABLE (MODELING.md)
    bevelThickness: o?.bevel ?? 0.004, bevelSize: o?.bevel ?? 0.004,
    bevelSegments: o?.seg ?? 2, curveSegments: 12,
  });
  geo.rotateY(Math.PI / 2);         // shape +X -> -Z (muzzle-ward), depth -> +X (across the gun)
  geo.translate(-depth / 2, 0, 0);  // centre the thickness on the gun's spine
  const e = new THREE.Mesh(geo, m);
  e.position.set(x, y, z);
  g.add(e);
  return e;
}
// TUBE along a CatmullRom through world-ish points (bow limbs, wire stocks, cable looms). 6+ points
// and 40 tubular segments per MODELING.md so curves are smooth and CONNECTED, not sausage links.
function tubeCurve(g: THREE.Group, pts: Array<[number, number, number]>, r: number, m: THREE.Material, radialSeg = 10, tubularSeg = 40): THREE.Mesh {
  const curve = new THREE.CatmullRomCurve3(pts.map(([px, py, pz]) => new THREE.Vector3(px, py, pz)));
  const e = new THREE.Mesh(new THREE.TubeGeometry(curve, tubularSeg, r, radialSeg, false), m);
  g.add(e);
  return e;
}
// A rounded-rectangle outline (receivers, plates, stocks start from this and get corners carved).
function rrect(w: number, h: number, r: number): THREE.Shape {
  const s = new THREE.Shape();
  const hw = w / 2, hh = h / 2;
  s.moveTo(-hw + r, -hh);
  s.lineTo(hw - r, -hh); s.quadraticCurveTo(hw, -hh, hw, -hh + r);
  s.lineTo(hw, hh - r); s.quadraticCurveTo(hw, hh, hw - r, hh);
  s.lineTo(-hw + r, hh); s.quadraticCurveTo(-hw, hh, -hw, hh - r);
  s.lineTo(-hw, -hh + r); s.quadraticCurveTo(-hw, -hh, -hw + r, -hh);
  return s;
}
// Punch rectangular LIGHTENING SLOTS into a shape (the signature ARC receiver panel: real holes whose
// cut edges catch light -- never painted on).
function slots(shape: THREE.Shape, xs: number[], y: number, w: number, h: number): THREE.Shape {
  for (const cx of xs) {
    const p = new THREE.Path();
    p.moveTo(cx - w / 2, y - h / 2);
    p.lineTo(cx + w / 2, y - h / 2);
    p.lineTo(cx + w / 2, y + h / 2);
    p.lineTo(cx - w / 2, y + h / 2);
    p.closePath();
    shape.holes.push(p);
  }
  return shape;
}
// A hex bolt head: a 6-segment lathe stub (reads as a hex from every angle). The ARC detail seasoning.
function bolt(g: THREE.Group, x: number, y: number, z: number, r = 0.008): void {
  lathe(g, [[r * 0.55, 0], [r, 0.002], [r, r * 0.9], [r * 0.62, r * 1.1]], x, y, z, GUNMETAL(), 6, 'z').rotation.y = Math.PI / 2;
}

// ================= long guns (two-handed): receiver ~y0.5, barrel forward to ~-0.8 =================
function receiver(g: THREE.Group, len: number, col: number): void {
  box(g, 0.072, 0.1, len, 0, 0.5, -0.28, STEEL(col));          // main receiver body
  for (const z of [-0.42, -0.28, -0.14]) rivet(g, 0.037, 0.52, z); // rivet row along the side
  grip(g, 0.05, 0.13, 0.05, 0, 0.41, -0.12, -0.32);           // tape-wrapped pistol grip
}
// AK: reuse the existing scrappy AK model (buildAkFp below) now that 'ak' is a real WeaponId.
function buildAk(g: THREE.Group): void { g.add(buildAkFp()); }

function buildSmg(g: THREE.Group): void {
  box(g, 0.07, 0.1, 0.3, 0, 0.5, -0.24, WOOD(0x7a5230));       // wood body (makeshift)
  box(g, 0.072, 0.055, 0.16, 0, 0.525, -0.12, STEEL(0x87837c)); // steel receiver top
  for (const z of [-0.34, -0.2, -0.08]) rivet(g, 0.037, 0.52, z);
  shroud(g, 0.05, 0.058, 0.26, 0, 0.52, -0.5, GUNMETAL());     // perforated barrel shroud
  rod(g, 0.014, 0.32, 0, 0.52, -0.52, STEEL(0x9a958c), 'z', 14); // barrel inside the shroud
  box(g, 0.05, 0.18, 0.075, 0, 0.38, -0.16, GUNMETAL(), 0.06); // box mag
  band(g, 0.032, 0.008, 0, 0.4, -0.16, TAPE());
  grip(g, 0.05, 0.12, 0.05, 0, 0.42, -0.06, -0.3);
  triggerGuard(g, 0, 0.44, -0.12);
  for (const z of [-0.34, -0.28, -0.22]) { const s = new THREE.Mesh(new THREE.ConeGeometry(0.013, 0.05, 4), GUNMETAL()); s.position.set(0, 0.565, z); g.add(s); } // top spikes
  box(g, 0.05, 0.07, 0.16, 0, 0.5, 0.04, SCRAP(0x5f636a));     // stubby stock
  box(g, 0.02, 0.03, 0.05, 0, 0.57, 0.02, GUNMETAL());         // rear sight
}
// PUMP SHOTGUN, ARC-tier rebuild. Parts list (FP frame, -Z = muzzle, receiver ~y0.5):
//   barrel group: lathe barrel + choke collar, perforated heat-shield plate (REAL holes) on standoffs,
//     lathe mag tube + end cap, 2 barrel/tube clamps + bolts, brass bead sight
//   receiver: extruded body (bevel), side panel w/ lightening slots, ejection port, loading gate plate,
//     4 rivets, welded scrap patch
//   pump: RIBBED lathe forend (wavy profile) + action bars either side
//   stock: extruded profile w/ grip throat, rubber pad, 2 tape wraps
//   details: side shell carrier w/ 3 red lathe shells, trigger guard + blade, sling loop, blue cable  (~45 parts)
function buildShotgun(g: THREE.Group): void {
  const steel = std(0x767068, 0.6, 0.4), gun = GUNMETAL(), wood = WOOD(0x5a3d22);
  // --- barrel group ---
  lathe(g, [[0.023, 0], [0.023, 0.44], [0.0205, 0.50]], 0, 0.545, -0.44, steel, 16);   // barrel
  lathe(g, [[0.021, 0], [0.026, 0.004], [0.026, 0.028], [0.022, 0.034]], 0, 0.545, -0.94, gun, 16); // choke collar
  { // perforated heat shield: a thin top plate with a row of REAL punched holes, on two standoffs
    const hs = rrect(0.30, 0.028, 0.008);
    for (const hx of [-0.10, -0.033, 0.033, 0.10]) { const p = new THREE.Path(); p.absarc(hx, 0, 0.009, 0, Math.PI * 2, false); hs.holes.push(p); }
    const m = extr(g, hs, 0.006, 0, 0.578, -0.62, SCRAP(0x5c6066));
    m.rotation.z = Math.PI / 2; // lay it flat over the barrel top (thickness -> up, holes punch downward)
    for (const zz of [-0.50, -0.74]) box(g, 0.006, 0.012, 0.01, 0, 0.567, zz, gun); // standoffs
  }
  lathe(g, [[0.0165, 0], [0.0165, 0.40], [0.019, 0.41], [0.019, 0.435]], 0, 0.492, -0.42, gun, 14); // mag tube + end cap
  for (const zz of [-0.47, -0.70]) { lathe(g, [[0.024, 0], [0.029, 0.004], [0.029, 0.016], [0.024, 0.02]], 0, 0.52, zz, steel, 14); bolt(g, 0.03, 0.52, zz + 0.01); } // clamps joining barrel+tube
  lathe(g, [[0.002, 0], [0.006, 0.004], [0.006, 0.018], [0.003, 0.024]], 0, 0.582, -0.925, std(0xd8b04a, 0.35, 0.7), 10, 'y'); // brass bead
  // --- receiver ---
  extr(g, rrect(0.30, 0.10, 0.02), 0.062, 0, 0.5, -0.16, steel, { bevel: 0.006 });
  extr(g, slots(rrect(0.20, 0.07, 0.012), [-0.04, 0.05], 0, 0.045, 0.03), 0.008, -0.036, 0.5, -0.17, SCRAP(0x60646b)); // left panel w/ slots
  extr(g, rrect(0.085, 0.034, 0.008), 0.006, 0.034, 0.515, -0.10, GUNMETAL()); // ejection port
  extr(g, rrect(0.07, 0.02, 0.006), 0.006, 0, 0.452, -0.12, gun);              // loading gate plate
  for (const zz of [-0.26, -0.06]) { rivet(g, 0.033, 0.53, zz); rivet(g, -0.033, 0.53, zz); }
  plate(g, 0.055, 0.055, 0.036, 0.487, -0.22, SCRAP(0x6a5a3a), 0.08);           // welded patch
  // --- pump: ribbed lathe forend (the wavy profile IS the ribbing) + action bars ---
  lathe(g, [[0.027, 0], [0.033, 0.012], [0.028, 0.024], [0.034, 0.036], [0.028, 0.048], [0.034, 0.06], [0.028, 0.072], [0.033, 0.084], [0.027, 0.10], [0.024, 0.115]], 0, 0.492, -0.30, wood, 14);
  for (const sx of [-1, 1]) box(g, 0.005, 0.012, 0.16, sx * 0.026, 0.492, -0.24, gun); // action bars
  // --- stock ---
  const stock = new THREE.Shape();
  stock.moveTo(-0.01, 0.05);
  stock.lineTo(-0.11, 0.042);
  stock.quadraticCurveTo(-0.19, 0.034, -0.22, 0.006);
  stock.lineTo(-0.23, -0.05);
  stock.quadraticCurveTo(-0.18, -0.075, -0.135, -0.056);
  stock.quadraticCurveTo(-0.08, -0.082, -0.042, -0.044);
  stock.quadraticCurveTo(-0.02, -0.024, 0.0, -0.032);
  stock.closePath();
  extr(g, stock, 0.05, 0, 0.5, 0.0, WOOD(0x6b4a2b), { bevel: 0.006 });
  extr(g, rrect(0.014, 0.088, 0.005), 0.052, 0, 0.478, 0.232, TAPE());          // rubber pad
  for (const zz of [0.06, 0.15]) band(g, 0.034, 0.007, 0, 0.487, zz, TAPE());   // tape wraps
  // --- details ---
  for (let i = 0; i < 3; i++) lathe(g, [[0.009, 0], [0.009, 0.036], [0.0105, 0.037], [0.0105, 0.048]], -0.045, 0.535 - 0.001 * i, -0.20 + i * 0.045, std(0xb23a2a, 0.7, 0.15), 10); // side-saddle shells (red, brass heads)
  extr(g, rrect(0.15, 0.028, 0.006), 0.005, -0.038, 0.535, -0.155, gun);        // shell carrier rail
  grip(g, 0.05, 0.12, 0.05, 0, 0.42, -0.04, -0.3);
  triggerGuard(g, 0, 0.443, -0.10);
  { const loop = new THREE.Mesh(new THREE.TorusGeometry(0.013, 0.004, 6, 12), gun); loop.position.set(0, 0.47, 0.24); loop.rotation.y = Math.PI / 2; g.add(loop); } // sling loop
  tubeCurve(g, [[0.036, 0.51, -0.28], [0.043, 0.52, -0.20], [0.040, 0.505, -0.12], [0.045, 0.515, -0.05], [0.038, 0.49, 0.0], [0.034, 0.475, 0.03]], 0.0032, std(0xc9a23a, 0.6, 0.1), 6, 40); // yellow wire
}
function buildDoubleBarrel(g: THREE.Group): void {
  box(g, 0.08, 0.1, 0.22, 0, 0.5, -0.18, STEEL(0x928d84));     // breech block
  for (const sx of [-0.027, 0.027]) { rod(g, 0.024, 0.6, sx, 0.5, -0.64, RUST(0x9a5a3a), 'z', 14); band(g, 0.028, 0.006, sx, 0.5, -0.72, GUNMETAL()); band(g, 0.028, 0.006, sx, 0.5, -0.5, GUNMETAL()); } // rusty side-by-side barrels
  box(g, 0.058, 0.11, 0.006, 0, 0.5, -0.36, GUNMETAL());       // rib between barrels
  for (let i = 0; i < 5; i++) band(g, 0.015, 0.005, 0, 0.575, -0.06 - i * 0.028, RUST(0xb0783a)); // exposed coil spring on top
  grip(g, 0.05, 0.12, 0.05, 0, 0.42, -0.06, -0.3);
  triggerGuard(g, 0, 0.44, -0.12);
  box(g, 0.08, 0.13, 0.24, 0, 0.49, 0.08, WOOD(0x6b4a2b), 0.05); // wood stock
  for (const o of [0.02, 0.1, 0.18]) band(g, 0.055, 0.008, 0, 0.49, o, TAPE());  // stock wraps
  rivet(g, 0.04, 0.56, -0.14); rivet(g, 0.04, 0.44, -0.14);    // hinge pins
}
// BOLT RIFLE, ARC-tier rebuild. Parts list (dims in FP frame: origin at grip, -Z = muzzle, receiver ~y0.5):
//   barrel group: tapered lathe barrel (z-0.42..-0.97), stepped muzzle brake, 2 lathe barrel bands + bolts,
//     cleaning rod, front sight base/post/hood
//   receiver: extruded body (bevel), 2 side panels WITH lightening slots, top rail + 6 teeth, ejection
//     port inset, bolt handle (tube curve) + lathe knob, 6 rivets, welded scrap patch
//   stock: ONE extruded wood profile (comb -> grip throat -> butt), rubber butt pad, cheek riser, 2 tape wraps
//   fore: wood forend, barrel strap + bolt
//   mag: extruded box + floorplate + release button
//   grip/trigger: guard loop + blade
//   details: 2 sling loops, blue cable loom (tube), hazard chip, rear sight block   (~50 parts)
function buildRifle(g: THREE.Group): void {
  const steel = std(0x7b766e, 0.62, 0.38), gun = GUNMETAL(), wood = WOOD(0x5a3a1e); // warm worn steel (rougher: no sky-mirror)
  // --- barrel group ---
  lathe(g, [[0.021, 0], [0.021, 0.06], [0.017, 0.30], [0.0145, 0.55]], 0, 0.52, -0.42, steel, 16); // tapered barrel
  lathe(g, [[0.015, 0], [0.024, 0.006], [0.024, 0.055], [0.0175, 0.062], [0.0175, 0.085]], 0, 0.52, -0.97, gun, 16); // stepped muzzle brake
  for (const zz of [-0.50, -0.72]) { lathe(g, [[0.019, 0], [0.0235, 0.004], [0.0235, 0.02], [0.019, 0.024]], 0, 0.52, zz, gun, 14); bolt(g, 0.024, 0.52, zz + 0.012); } // bands + bolts
  rod(g, 0.006, 0.4, 0, 0.487, -0.62, gun, 'z', 8);                       // cleaning rod under the barrel
  extr(g, rrect(0.05, 0.03, 0.008), 0.02, 0, 0.55, -0.90, gun);            // front sight base
  box(g, 0.006, 0.032, 0.006, 0, 0.585, -0.90, gun);                       // sight post
  band(g, 0.02, 0.004, 0, 0.585, -0.905, steel);                           // sight hood ring
  // --- receiver ---
  extr(g, rrect(0.44, 0.105, 0.02), 0.066, 0, 0.5, -0.26, steel, { bevel: 0.006 }); // main body
  for (const sx of [-1, 1]) extr(g, slots(rrect(0.30, 0.076, 0.012), [-0.06, 0.04, 0.14], 0, 0.052, 0.034), 0.008, sx * 0.038, 0.5, -0.28, SCRAP(0x60646b)); // side panels w/ REAL lightening slots
  extr(g, rrect(0.30, 0.016, 0.005), 0.034, 0, 0.565, -0.26, gun);          // top rail spine
  for (let i = 0; i < 6; i++) box(g, 0.036, 0.008, 0.02, 0, 0.577, -0.14 - i * 0.05, gun); // rail teeth
  extr(g, rrect(0.09, 0.036, 0.008), 0.006, 0.036, 0.515, -0.16, GUNMETAL()); // ejection port inset (right side)
  tubeCurve(g, [[0.03, 0.53, -0.10], [0.065, 0.53, -0.095], [0.085, 0.515, -0.075], [0.09, 0.49, -0.05], [0.088, 0.465, -0.03], [0.084, 0.45, -0.02]], 0.007, gun, 8, 40); // bolt handle sweep
  lathe(g, [[0.004, 0], [0.014, 0.006], [0.016, 0.02], [0.011, 0.032], [0.002, 0.036]], 0.084, 0.45, -0.02, steel, 12, 'y'); // bolt knob
  for (const zz of [-0.42, -0.30, -0.18]) { rivet(g, 0.035, 0.53, zz); rivet(g, -0.035, 0.53, zz); }
  plate(g, 0.06, 0.07, 0.043, 0.49, -0.36, SCRAP(0x6a5a3a), 0.06);          // welded scrap patch
  // --- stock: one extruded wood PROFILE (the silhouette win), rubber pad, cheek riser, tape ---
  const stock = new THREE.Shape();
  stock.moveTo(-0.02, 0.055);                       // top, at the receiver's rear
  stock.lineTo(-0.11, 0.046);                       // comb run
  stock.quadraticCurveTo(-0.19, 0.04, -0.225, 0.01);  // slope to the heel
  stock.lineTo(-0.235, -0.052);                     // butt face
  stock.quadraticCurveTo(-0.19, -0.078, -0.145, -0.058); // toe curve
  stock.quadraticCurveTo(-0.09, -0.084, -0.05, -0.046);  // grip throat dip
  stock.quadraticCurveTo(-0.026, -0.026, -0.01, -0.036);
  stock.lineTo(0.0, -0.012);
  stock.closePath();
  extr(g, stock, 0.052, 0, 0.5, 0.0, wood, { bevel: 0.006 });               // (shape x<0 extends rearward = +Z; ends ~z0.235 so it never near-clips the FP camera)
  extr(g, rrect(0.014, 0.095, 0.005), 0.054, 0, 0.478, 0.238, TAPE());      // rubber butt pad
  extr(g, rrect(0.08, 0.013, 0.005), 0.036, 0, 0.542, 0.11, WOOD(0x4a3320)); // cheek riser (hugs the comb)
  for (const zz of [0.06, 0.15]) band(g, 0.034, 0.007, 0, 0.487, zz, TAPE()); // stock tape wraps
  // --- forend + strap ---
  extr(g, rrect(0.26, 0.045, 0.014), 0.07, 0, 0.468, -0.42, wood, { bevel: 0.006 });
  band(g, 0.042, 0.006, 0, 0.49, -0.50, gun); bolt(g, 0.045, 0.468, -0.50); // strap + bolt
  // --- mag + trigger ---
  extr(g, rrect(0.075, 0.075, 0.01), 0.046, 0, 0.415, -0.18, gun);          // box mag
  extr(g, rrect(0.08, 0.014, 0.005), 0.05, 0, 0.372, -0.18, GUNMETAL());    // floorplate
  box(g, 0.012, 0.014, 0.02, 0.033, 0.44, -0.13, RUST(0xa04a30));           // mag release button
  grip(g, 0.05, 0.13, 0.05, 0, 0.41, -0.10, -0.32);                          // wrapped pistol grip
  triggerGuard(g, 0, 0.44, -0.14);
  // --- detail seasoning ---
  for (const zz of [-0.86, 0.24] as const) { const loop = new THREE.Mesh(new THREE.TorusGeometry(0.014, 0.004, 6, 12), gun); loop.position.set(0, zz < 0 ? 0.495 : 0.45, zz); loop.rotation.y = Math.PI / 2; g.add(loop); } // sling loops
  tubeCurve(g, [[-0.037, 0.52, -0.40], [-0.045, 0.53, -0.32], [-0.043, 0.515, -0.22], [-0.048, 0.525, -0.12], [-0.042, 0.505, -0.05], [-0.038, 0.49, 0.0]], 0.0035, std(0x2f5a9a, 0.6, 0.1), 6, 40); // blue cable loom
  extr(g, rrect(0.03, 0.02, 0.004), 0.004, -0.036, 0.475, -0.34, std(0xb23a2a, 0.85, 0.05)); // red hazard chip
  box(g, 0.024, 0.02, 0.02, 0, 0.575, -0.05, gun);                           // rear sight block
}
function buildSniper(g: THREE.Group): void {
  receiver(g, 0.46, 0x33363b);
  rod(g, 0.016, 0.78, 0, 0.52, -0.76, STEEL(0xa39d92), 'z', 16); // very long round barrel
  rod(g, 0.02, 0.08, 0, 0.52, -0.98, GUNMETAL(), 'z', 12);     // muzzle brake
  // scope: a round tube on two mounts, dark glass ends
  rod(g, 0.032, 0.22, 0, 0.62, -0.34, GUNMETAL(), 'z', 14); band(g, 0.036, 0.008, 0, 0.62, -0.24, GUNMETAL());
  const lens = new THREE.Mesh(new THREE.CircleGeometry(0.028, 14), std(0x2a3f5a, 0.1, 0.4)); lens.position.set(0, 0.62, -0.45); g.add(lens);
  for (const z of [-0.28, -0.4]) box(g, 0.03, 0.09, 0.03, 0, 0.565, z, GUNMETAL()); // scope mounts
  box(g, 0.07, 0.13, 0.3, 0, 0.47, 0.06, WOOD(0x4a3320), 0.04); // stock
  plate(g, 0.05, 0.09, 0, 0.47, 0.0, SCRAP(0x55463a));
}
// ROCKET LAUNCHER, ARC-tier rebuild. Parts list (FP frame, -Z = muzzle, tube ~y0.5):
//   tube: ONE stepped lathe (rear vent -> body -> throat -> flared bell), 3 weld-band lathes + hex
//     bolts, warhead lathe (rust) w/ band poking out the muzzle
//   furniture: shoulder rest (extruded profile + pad), front grip post, wrapped rear grip, trigger
//     housing (extrude w/ bevel) + guard + blade
//   sight: flip-frame w/ REAL peep hole + lens disc, on a riser
//   rear: 4 exhaust fins (extruded wedges), vent ring
//   details: carry handle (tube), 2 rivet rows, hazard chevron chips, blue cable loom, stencil plate  (~42 parts)
function buildRocketLauncher(g: THREE.Group): void {
  const shell = SCRAP(0x566150), gun = GUNMETAL();
  // --- the tube: one continuous stepped lathe from rear vent to flared bell ---
  lathe(g, [
    [0.052, 0], [0.066, 0.012], [0.066, 0.06], [0.058, 0.09],   // rear vent flare + collar
    [0.058, 0.72], [0.052, 0.78],                                // long body -> throat
    [0.072, 0.90], [0.078, 0.97], [0.070, 0.98],                 // flared bell mouth
  ], 0, 0.5, 0.08, shell, 18);
  for (const zz of [-0.12, -0.38, -0.62]) { lathe(g, [[0.059, 0], [0.067, 0.005], [0.067, 0.022], [0.059, 0.027]], 0, 0.5, zz, gun, 18); for (const a of [0.6, 2.6, 4.6]) bolt(g, Math.cos(a) * 0.066, 0.5 + Math.sin(a) * 0.066, zz + 0.013); } // weld bands + hex bolts
  lathe(g, [[0.008, 0], [0.030, 0.03], [0.030, 0.07], [0.012, 0.10], [0.012, 0.13]], 0, 0.5, -0.88, RUST(0x9a4a30), 14); // warhead tip
  band(g, 0.032, 0.006, 0, 0.5, -0.93, gun);                                    // warhead retaining band
  // --- shoulder rest + grips + trigger ---
  const rest = new THREE.Shape();
  rest.moveTo(0.02, 0.0); rest.lineTo(0.02, -0.10);
  rest.quadraticCurveTo(0.0, -0.13, -0.05, -0.125);
  rest.lineTo(-0.09, -0.115); rest.quadraticCurveTo(-0.11, -0.06, -0.10, -0.005);
  rest.closePath();
  extr(g, rest, 0.05, 0, 0.44, 0.02, shell, { bevel: 0.005 });                   // under-tube shoulder rest
  extr(g, rrect(0.02, 0.11, 0.007), 0.056, 0, 0.385, 0.075, TAPE());             // rest pad (rubber)
  grip(g, 0.05, 0.13, 0.05, 0, 0.40, -0.12, -0.3);                                // wrapped rear grip
  rod(g, 0.014, 0.10, 0, 0.40, -0.30, WOOD(0x5a3d22), 'y', 10);                   // front grip post
  for (const o of [-0.03, 0.0, 0.03]) band(g, 0.017, 0.005, 0, 0.40 + o, -0.30, TAPE()); // post wraps
  { const th = rrect(0.10, 0.05, 0.012); extr(g, th, 0.04, 0, 0.435, -0.20, gun); } // trigger housing
  triggerGuard(g, 0, 0.425, -0.18);
  // --- flip sight w/ a REAL peep hole ---
  { const sf = rrect(0.036, 0.05, 0.008); const p = new THREE.Path(); p.absarc(0, 0.008, 0.009, 0, Math.PI * 2, false); sf.holes.push(p);
    const m = extr(g, sf, 0.006, 0.062, 0.60, -0.30, gun); m.rotation.y = Math.PI / 2; } // frame faces the shooter
  box(g, 0.008, 0.035, 0.008, 0.062, 0.565, -0.30, gun);                          // sight riser
  // --- rear exhaust fins ---
  for (const a of [0.79, 2.36, 3.93, 5.50]) {
    const fin = extr(g, rrect(0.05, 0.016, 0.005), 0.006, 0, 0.5, 0.13, SCRAP(0x4c5648));
    fin.position.set(Math.cos(a) * 0.055, 0.5 + Math.sin(a) * 0.055, 0.13);
    fin.rotation.z = a; // radiating outward
  }
  // --- detail seasoning ---
  tubeCurve(g, [[0, 0.575, -0.52], [0, 0.60, -0.44], [0, 0.615, -0.34], [0, 0.615, -0.24], [0, 0.60, -0.16], [0, 0.578, -0.10]], 0.008, gun, 8, 40); // carry handle arc
  for (const zz of [-0.26, -0.50]) for (const sx of [-1, 1]) rivet(g, sx * 0.052, 0.545, zz);
  for (let i = 0; i < 3; i++) extr(g, rrect(0.02, 0.014, 0.003), 0.004, -0.062, 0.47 + i * 0.001, -0.70 + i * 0.032, std(i % 2 ? 0x2a2a2a : 0xd9a23a, 0.85, 0.05)); // hazard chevrons
  tubeCurve(g, [[0.055, 0.47, -0.60], [0.062, 0.485, -0.48], [0.058, 0.47, -0.36], [0.063, 0.48, -0.24], [0.056, 0.465, -0.14], [0.052, 0.455, -0.06]], 0.0035, std(0x2f5a9a, 0.6, 0.1), 6, 40); // blue cable
  extr(g, rrect(0.07, 0.022, 0.004), 0.004, 0.058, 0.52, -0.30, std(0xb9c08a, 0.85, 0.02)); // stencil plate
}
// ================= bows (two-handed) =================
// HUNTING BOW rebuilt per MODELING.md's tube rules: each limb is ONE TubeGeometry along a CatmullRom
// with 7 control points (SHARING the riser point -- no floating sausage links) at 40 tubular segments.
// Parts: 2 tube limbs, lathe tip nocks, string (2 thin tubes meeting at the nock), extruded riser with
// an arrow shelf, grip wraps, nocked arrow (shaft rod + lathe head + 2 fletch vanes), limb lashings.
function buildBow(g: THREE.Group): void {
  const wood = WOOD(0x6b4a28);
  // Limbs: recurve sweep from the shared riser point out to the tips (7 points each, connected).
  const TIP_TOP: [number, number, number] = [0, 1.02, -0.33];
  const TIP_BOT: [number, number, number] = [0, -0.02, -0.33];
  tubeCurve(g, [[0, 0.545, -0.37], [0, 0.64, -0.395], [0, 0.74, -0.415], [0, 0.84, -0.42], [0, 0.93, -0.405], [0, 0.99, -0.37], TIP_TOP], 0.014, wood, 10, 40);
  tubeCurve(g, [[0, 0.455, -0.37], [0, 0.36, -0.395], [0, 0.26, -0.415], [0, 0.16, -0.42], [0, 0.07, -0.405], [0, 0.01, -0.37], TIP_BOT], 0.014, wood, 10, 40);
  for (const [tx, ty, tz] of [TIP_TOP, TIP_BOT]) lathe(g, [[0.006, 0], [0.011, 0.008], [0.011, 0.02], [0.004, 0.03]], tx, ty, tz, WOOD(0x4a3320), 10, 'y'); // tip nocks
  // String: two taut runs from the tips to the nock point (thin tubes, not boxes).
  const NOCK: [number, number, number] = [0, 0.5, -0.26];
  tubeCurve(g, [TIP_TOP, [0, 0.76, -0.295], NOCK], 0.0035, std(0xd8cba0, 0.85, 0.0), 6, 24);
  tubeCurve(g, [NOCK, [0, 0.24, -0.295], TIP_BOT], 0.0035, std(0xd8cba0, 0.85, 0.0), 6, 24);
  // Riser: an extruded profile with a cut ARROW SHELF (flat -> extrude with bevel, per the class table).
  const riser = new THREE.Shape();
  riser.moveTo(0.028, 0.10); riser.lineTo(0.028, -0.10);
  riser.quadraticCurveTo(0.01, -0.13, -0.012, -0.10);
  riser.lineTo(-0.012, -0.02);
  riser.lineTo(0.004, 0.012);   // the shelf step the arrow rides on
  riser.lineTo(-0.012, 0.03);
  riser.lineTo(-0.012, 0.10);
  riser.quadraticCurveTo(0.008, 0.125, 0.028, 0.10);
  riser.closePath();
  { const m = extr(g, riser, 0.034, 0, 0.5, -0.375, WOOD(0x7a5230), { bevel: 0.005 }); m.rotation.y = Math.PI / 2; } // profile faces the shooter
  for (const o of [-0.045, 0, 0.045]) band(g, 0.022, 0.006, 0, 0.5 + o, -0.375, TAPE()); // grip wraps
  for (const o of [0.56, 0.44]) band(g, 0.016, 0.005, 0, o + 0.0, -0.372, RUST(0x8a5230)); // limb lashings at the riser joins
  // Nocked arrow along -Z: shaft, lathe broadhead, two fletch vanes.
  rod(g, 0.0055, 0.6, 0, 0.5, -0.52, WOOD(0x8a6a3f), 'z', 8);
  lathe(g, [[0.002, 0], [0.017, 0.028], [0.006, 0.05], [0.001, 0.068]], 0, 0.5, -0.80, GUNMETAL(), 8); // broadhead
  for (const rot of [0.5, -0.5]) { const v = extr(g, rrect(0.05, 0.016, 0.004), 0.0016, 0, 0.5, -0.245, std(0xb23a3a, 0.9, 0)); v.rotation.z = rot; } // fletching vanes
}
function buildCrossbow(g: THREE.Group): void {
  const wood = WOOD(0x6b5236);
  box(g, 0.055, 0.06, 0.58, 0, 0.5, -0.26, wood);             // main stock body
  box(g, 0.09, 0.075, 0.16, 0, 0.5, 0.06, wood, 0.06);        // rear butt (angled down)
  box(g, 0.03, 0.03, 0.5, 0, 0.535, -0.34, GUNMETAL());       // top rail (bolt groove)
  // horizontal prod: two limbs sweeping forward from a hub at the front of the rail
  for (const dir of [1, -1]) {
    let x = 0, z = -0.52, ang = 0.15;
    for (let i = 0; i < 3; i++) { const len = 0.11, r = 0.016 - i * 0.003; const cx = x + dir * Math.cos(ang) * len / 2, cz = z + Math.sin(ang) * len / 2; const s = new THREE.Mesh(new THREE.CylinderGeometry(r, r + 0.001, len, 7), STEEL(0x928d84)); s.position.set(cx, 0.5, cz); s.rotation.z = dir === 1 ? Math.PI / 2 - ang : Math.PI / 2 + ang; g.add(s); x += dir * Math.cos(ang) * len; z += Math.sin(ang) * len; ang += 0.28; }
  }
  box(g, 0.5, 0.006, 0.006, 0, 0.5, -0.5, std(0xd8cba0, 0.85, 0.0)); // string across the prod tips
  box(g, 0.008, 0.008, 0.4, 0, 0.552, -0.4, wood);            // loaded bolt in the groove
  const bh = new THREE.Mesh(new THREE.ConeGeometry(0.014, 0.05, 5), GUNMETAL()); bh.rotation.x = -Math.PI / 2; bh.position.set(0, 0.552, -0.62); g.add(bh); // bolt head
  rod(g, 0.02, 0.14, 0.06, 0.6, -0.28, GUNMETAL(), 'z', 12);  // small top scope tube
  for (const z of [-0.22, -0.34]) box(g, 0.02, 0.06, 0.02, 0.06, 0.565, z, GUNMETAL()); // scope mounts
  grip(g, 0.05, 0.12, 0.05, 0, 0.42, -0.06, -0.3);            // pistol grip
  triggerGuard(g, 0, 0.44, -0.12);
}
// ================= one-handed =================
function buildPistol(g: THREE.Group): void {
  box(g, 0.052, 0.062, 0.24, 0, 0.18, -0.15, STEEL(0x87837c)); // slide
  box(g, 0.05, 0.03, 0.11, 0, 0.216, -0.06, SCRAP(0xb0902a));  // painted scrap top block (Skippy yellow)
  box(g, 0.046, 0.016, 0.24, 0, 0.205, -0.15, GUNMETAL());     // slide serration rib
  rod(g, 0.014, 0.12, 0, 0.18, -0.31, GUNMETAL(), 'z', 14);    // barrel out the front
  rod(g, 0.018, 0.028, 0, 0.18, -0.38, GUNMETAL(), 'z', 12);   // muzzle collar
  grip(g, 0.05, 0.15, 0.06, 0, 0.075, -0.02, 0.28);           // tape-wrapped grip
  triggerGuard(g, 0, 0.12, -0.06);
  plate(g, 0.04, 0.045, 0.026, 0.18, -0.13, SCRAP(0x9a3a30));  // red accent side plate
  box(g, 0.012, 0.018, 0.018, 0, 0.235, -0.27, GUNMETAL());    // front sight
  box(g, 0.022, 0.02, 0.02, 0, 0.235, -0.04, GUNMETAL());      // rear sight
}
function buildSword(g: THREE.Group): void {
  grip(g, 0.028, 0.22, 0.028, 0, 0.11, 0, 0);                 // wrapped handle
  box(g, 0.16, 0.035, 0.045, 0, 0.24, 0, GUNMETAL());         // crossguard
  box(g, 0.05, 0.6, 0.014, 0, 0.55, 0, STEEL(0xc2c8d0));      // shiny blade
  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.026, 0.12, 4), STEEL(0xc2c8d0)); tip.position.set(0, 0.9, 0); g.add(tip);
  box(g, 0.012, 0.5, 0.02, 0, 0.55, 0, std(0x9aa0a8, 0.25, 0.9)); // bright fuller stripe
}
// Matched to the owner's mace.png icon: a round steel ball studded with pyramid spikes on a long WOOD
// haft, dark wrap only at the bottom grip (the old model was all-wrap with a lumpy scrap head).
function buildMace(g: THREE.Group): void {
  rod(g, 0.024, 0.34, 0, 0.28, 0, WOOD(0x7a5230), 'y', 8);    // bare wooden haft
  grip(g, 0.034, 0.16, 0.034, 0, 0.08, 0, 0);                 // wrapped grip at the bottom
  band(g, 0.045, 0.014, 0, 0.155, 0, GUNMETAL());             // wrap collar
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.105, 10, 8), STEEL(0x848b94)); head.position.set(0, 0.5, 0); g.add(head); // steel ball
  // Spikes: an equator ring, an upper + lower ring, and one on top -- reads spiked from every angle.
  const spike = (dir: THREE.Vector3): void => {
    const s = new THREE.Mesh(new THREE.ConeGeometry(0.024, 0.075, 4), GUNMETAL());
    s.position.set(0, 0.5, 0).addScaledVector(dir, 0.105);
    s.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    g.add(s);
  };
  for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; spike(new THREE.Vector3(Math.cos(a), 0, Math.sin(a))); }
  for (const ky of [0.62, -0.62]) for (let i = 0; i < 4; i++) { const a = (i / 4) * Math.PI * 2 + 0.4; spike(new THREE.Vector3(Math.cos(a) * 0.78, ky, Math.sin(a) * 0.78).normalize()); }
  spike(new THREE.Vector3(0, 1, 0));
  band(g, 0.05, 0.014, 0, 0.39, 0, GUNMETAL());               // collar under the head
}
function buildSpear(g: THREE.Group, tipColor: number): void {
  const shaft = rod(g, 0.017, 0.95, 0, 0.36, -0.3, WOOD(0x6b4a2b), 'y', 8); shaft.rotation.x = Math.PI / 2.3; // long shaft, laid forward
  for (const o of [-0.15, 0.05, 0.25]) { const t = new THREE.Mesh(new THREE.TorusGeometry(0.02, 0.006, 6, 10), TAPE()); t.position.set(0, 0.36 + Math.cos(Math.PI / 2.3) * o, -0.3 - Math.sin(Math.PI / 2.3) * o); t.rotation.x = Math.PI / 2.3; g.add(t); } // lashings
  const head = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.22, 6), std(tipColor, 0.4, 0.6)); head.rotation.x = -Math.PI / 2; head.position.set(0, 0.5, -0.72); g.add(head); // leaf head
  box(g, 0.012, 0.09, 0.02, 0, 0.5, -0.64, std(tipColor, 0.4, 0.6)); // barb
}
function buildGrenade(g: THREE.Group): void {
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.075, 12, 8), SCRAP(0x46503a)); body.position.set(0, 0.14, -0.04); g.add(body);
  for (let i = 0; i < 3; i++) band(g, 0.076, 0.007, 0, 0.14 + (i - 1) * 0.04, -0.04, GUNMETAL()); // grooves
  rod(g, 0.02, 0.05, 0, 0.21, -0.04, GUNMETAL(), 'y', 8);     // fuse cap
  box(g, 0.014, 0.07, 0.014, 0.045, 0.2, -0.04, STEEL(0x9aa0a8)); // spoon/lever
}
function buildBrick(g: THREE.Group, c: number, detonator: boolean): void {
  box(g, 0.18, 0.1, 0.1, 0, 0.14, -0.04, std(c, 0.9, 0.02, true)); // block of explosive
  for (const o of [-0.05, 0.05]) box(g, 0.04, 0.115, 0.11, o, 0.14, -0.04, TAPE()); // binding straps
  if (detonator) { box(g, 0.04, 0.05, 0.04, 0.055, 0.21, -0.04, RUST(0xc24a3a)); rod(g, 0.004, 0.06, 0.055, 0.27, -0.04, GUNMETAL(), 'y', 5); } // detonator + antenna
  else { rod(g, 0.02, 0.14, 0, 0.14, 0.06, RUST(0x7a5a3a), 'z', 8); } // satchel: a fuse cord
}

// Owner-modelled GLBs (2026-07-09) replacing the procedural shotguns below (kept as the instant-load
// fallback for the very first equip of a session, before the model warms). Raw exports are centred near
// their own origin with the barrel along local +X (length ~1.9) -- the FP frame wants the barrel along
// -Z instead, so FIT rotates 90deg to swap those axes, then offsets the (already scaled) model up/back to
// land where the procedural gun's own body sits relative to the grip-at-origin convention (see buildShotgun/
// buildDoubleBarrel above: their parts cluster around y~0.47-0.5, biased toward -Z/the muzzle). fallbackScale
// is an informed guess (raw length / the procedural gun's own length) so a fresh clone isn't comically
// oversized before the owner dials buildable_resizers.json in via a live check.
const WEAPON_GLB: Partial<Record<WeaponId, { file: string; fallbackScale: number; yOffset: number; zOffset: number }>> = {
  shotgun: { file: 'shotgun_pump.glb', fallbackScale: 0.6, yOffset: 0.47, zOffset: -0.35 },
  double_barrel: { file: 'shotgun_double_barrel.glb', fallbackScale: 0.5, yOffset: 0.48, zOffset: -0.28 },
  // Pistols sit much lower/shorter than a shotgun's raised receiver+stock (see buildPistol: grip at y0.075
  // vs. a shotgun's grip at y0.42) -- smaller fallbackScale + yOffset to match.
  pistol: { file: 'pistol.glb', fallbackScale: 0.22, yOffset: 0.15, zOffset: -0.18 },
  // Owner-modelled GLBs (2026-07-10) replacing the procedural AK (buildAkFp below, kept as the instant-load
  // fallback) and the procedural rifle -- 'rifle' IS the "Bolt Rifle" WeaponId (see weapons.ts), which is
  // what bolt_rifle.glb models. Both are long two-handed guns like the shotguns above, so seeded from the
  // same ballpark yOffset/zOffset until the owner dials the real values in via F4 -> OBJECTS -> Buildables.
  ak: { file: 'ak.glb', fallbackScale: 0.6, yOffset: 0.47, zOffset: -0.3 },
  rifle: { file: 'bolt_rifle.glb', fallbackScale: 0.6, yOffset: 0.47, zOffset: -0.32 },
  // Owner-modelled GLBs (2026-07-11): the remaining guns + the C4 brick (held model; the thrown sticky
  // projectile clones the same file in engine/projectiles.ts).
  smg: { file: 'smg.glb', fallbackScale: 0.38, yOffset: 0.47, zOffset: -0.26 }, // 0.5 read comically oversized in hand (owner)
  sniper: { file: 'sniper.glb', fallbackScale: 0.62, yOffset: 0.47, zOffset: -0.34 },
  crossbow: { file: 'crossbow.glb', fallbackScale: 0.55, yOffset: 0.47, zOffset: -0.28 },
  c4: { file: 'c4.glb', fallbackScale: 0.3, yOffset: 0.16, zOffset: -0.08 },
  satchel_charge: { file: 'satchel.glb', fallbackScale: 0.3, yOffset: 0.16, zOffset: -0.08 },
  grenade: { file: 'grenade.glb', fallbackScale: 0.22, yOffset: 0.16, zOffset: -0.08 },
  // Owner-modelled bow + spears (2026-07-11). The bow gets a live string + nocked arrow added on top of the
  // GLB (dressBow below); the spears are melee/throw so they sit lower in the grip than a long gun.
  bow: { file: 'bow.glb', fallbackScale: 0.55, yOffset: 0.47, zOffset: -0.1 },
  spear: { file: 'spear_wooden.glb', fallbackScale: 0.7, yOffset: 0.3, zOffset: -0.1 },
  stone_spear: { file: 'spear_stone.glb', fallbackScale: 0.7, yOffset: 0.3, zOffset: -0.1 },
  sword: { file: 'sword.glb', fallbackScale: 0.45, yOffset: 0.25, zOffset: -0.08 },
  mace: { file: 'mace.glb', fallbackScale: 0.45, yOffset: 0.25, zOffset: -0.08 },
};
for (const w of Object.values(WEAPON_GLB)) preloadGLB('/models/' + w!.file);

function fitGLBWeapon(scene: THREE.Object3D, spec: { file: string; fallbackScale: number; yOffset: number; zOffset: number }, id?: string): THREE.Object3D {
  const fit0 = weaponFit(id);
  const wrap = new THREE.Group();
  scene.rotation.y = -Math.PI / 2; // raw barrel-along-+X -> FP barrel-along--Z
  wrap.add(scene);
  const scale = buildableScale(spec.file, spec.fallbackScale);
  wrap.scale.setScalar(scale);
  if (fit0.scale) wrap.scale.multiplyScalar(fit0.scale); // F8 scale sliders: overall + per-axis (w/h/l)
  if (fit0.scl) wrap.scale.multiply(new THREE.Vector3(fit0.scl[0] || 1, fit0.scl[1] || 1, fit0.scl[2] || 1));
  // Per-gun fit (F4 -> Objects -> Guns): position/rotation overrides on top of the class defaults.
  const fit = weaponFit(id);
  if (fit.rot) wrap.rotation.set(fit.rot[0], fit.rot[1], fit.rot[2]);
  if (fit.fp) wrap.position.set(fit.fp[0], fit.fp[1], fit.fp[2]);
  else wrap.position.set(0, spec.yOffset, spec.zOffset);
  wrap.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.castShadow = true; });
  return wrap;
}

// Dress the GLB bow with a live string + a nocked arrow, both derived from the model's own geometry:
// the string spans the limb-tip clusters (topmost/bottommost 6% of the bow's height), the nock rests at
// their midpoint, and the arrow lies nocked pointing -Z. Viewmodel.update() animates the named nodes --
// 'bowString' (3-point line: top tip, nock, bottom tip) pulls its middle point back (+Z) with the draw,
// and 'bowArrow' (visible only while drawing) slides back with it.
function dressBow(g: THREE.Group): void {
  const box = new THREE.Box3(); box.makeEmpty();
  eachVertexLocal(g, (v) => box.expandByPoint(v));
  if (box.isEmpty()) return;
  const h = box.max.y - box.min.y, band = h * 0.06;
  const top = new THREE.Vector3(), bot = new THREE.Vector3(); let nTop = 0, nBot = 0;
  eachVertexLocal(g, (v) => {
    if (v.y > box.max.y - band) { top.add(v); nTop++; }
    else if (v.y < box.min.y + band) { bot.add(v); nBot++; }
  });
  if (!nTop || !nBot) return;
  top.divideScalar(nTop); bot.divideScalar(nBot);
  const rest = top.clone().add(bot).multiplyScalar(0.5);
  const stringGeo = new THREE.BufferGeometry().setFromPoints([top, rest, bot]);
  const string = new THREE.Line(stringGeo, new THREE.LineBasicMaterial({ color: 0x17140f }));
  string.name = 'bowString';
  string.userData.tips = { top: top.toArray(), bot: bot.toArray(), rest: rest.toArray(), pull: h * 0.42 };
  g.add(string);
  // Nocked arrow (mirrors the projectile arrow's proportions), tail at the nock, tip toward -Z.
  // VISIBLE by default: the 3rd-person / remote-player bow shows an arrow nocked whenever the bow is
  // held (playtest 2026-07-13: arrows were 1P-only). The FP viewmodel re-drives this flag every frame
  // from its draw state (viewmodel.ts), so first person still shows it only while drawing.
  const arrow = new THREE.Group(); arrow.name = 'bowArrow'; arrow.visible = true;
  const lam = (c: number): THREE.MeshLambertMaterial => new THREE.MeshLambertMaterial({ color: c, flatShading: true });
  const len = 0.7;
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.011, 0.009, len, 6), lam(0x6b4a2b)); shaft.rotation.x = Math.PI / 2; shaft.position.z = -len / 2; arrow.add(shaft);
  const head = new THREE.Mesh(new THREE.ConeGeometry(0.032, 0.16, 6), lam(0x9aa0a6)); head.rotation.x = -Math.PI / 2; head.position.z = -len - 0.06; arrow.add(head);
  const fl = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.001, 0.11), lam(0xb23a3a)); fl.position.z = -0.07; arrow.add(fl);
  arrow.position.copy(rest);
  g.add(arrow);
}

function buildInto(g: THREE.Group, id: WeaponId): void {
  const glbSpec = WEAPON_GLB[id];
  if (glbSpec) {
    const ready = cloneGLBIfReady('/models/' + glbSpec.file);
    if (ready) {
      // C4/satchel are held FACE TOWARD THE CAMERA (owner: "so I can see it") -- tip the top face back
      // toward the eye instead of lying flat like a gun. ADD to the fit rotation (never overwrite it),
      // so the F8 rot sliders still work on these.
      if (id === 'c4' || id === 'satchel_charge' || id === 'grenade') {
        const fitted = fitGLBWeapon(ready, glbSpec, id);
        fitted.rotation.x += 0.85; // roll the top of the charge back toward the camera
        g.add(fitted);
        return;
      }
      // Swung melee stands UPRIGHT in the fist like a tool; everything else lies gun-wise along -Z.
      // The F8 fp/rot/scale fit applies on a wrapper of its own (owner: F8 must work for EVERY handheld),
      // leaving `g` clean for buildWeapon3p's p3/p3r pass.
      if (id === 'sword' || id === 'mace') {
        const fitted = fitGLBHeld(ready, glbSpec.file, glbSpec.fallbackScale);
        // Sword's blade sits at raw -X (like the axe/pick heads); the mace was exported the other way round.
        fitted.rotation.z = id === 'mace' ? Math.PI / 2 : -Math.PI / 2;
        fitted.position.y = 0.12;
        const inner = new THREE.Group();
        inner.add(fitted);
        applyHeldFit(inner, id, false);
        g.add(inner);
        return;
      }
      g.add(fitGLBWeapon(ready, glbSpec, id));
      if (id === 'bow') dressBow(g);
      return;
    }
  }
  switch (id) {
    case 'smg': return buildSmg(g);
    case 'ak': return buildAk(g);
    case 'shotgun': return buildShotgun(g);
    case 'double_barrel': return buildDoubleBarrel(g);
    case 'rifle': return buildRifle(g);
    case 'sniper': return buildSniper(g);
    case 'rocket_launcher': return buildRocketLauncher(g);
    case 'bow': return buildBow(g);
    case 'crossbow': return buildCrossbow(g);
    case 'pistol': return buildPistol(g);
    case 'sword': return buildSword(g);
    case 'mace': return buildMace(g);
    case 'spear': return buildSpear(g, WEAPONS.spear.color);
    case 'stone_spear': return buildSpear(g, WEAPONS.stone_spear.color);
    case 'grenade': return buildGrenade(g);
    case 'c4': return buildBrick(g, WEAPONS.c4.color, true);
    case 'satchel_charge': return buildBrick(g, WEAPONS.satchel_charge.color, false);
  }
}

// First-person held model (origin at grip; barrel along -Z, pointing at the crosshair).
export function buildWeaponFp(id: WeaponId): THREE.Group { const g = new THREE.Group(); buildInto(g, id); return g; }

// Third-person hand model: the same geometry re-seated onto the lower 3P hand socket.
export function buildWeapon3p(id: WeaponId): THREE.Group {
  const g = new THREE.Group();
  buildInto(g, id);
  g.position.y -= WEAPONS[id].twoHanded ? 0.5 : 0.12; // drop from the FP hand height onto the 3P hand
  // Record the DEFAULT seat before the fit lands: Player's per-frame pass recomputes the ABSOLUTE
  // rest/aim transforms from weaponFit (p3/p3r vs p3Aim/p3rAim) on top of this, blending as the body
  // raises to aim -- four independent view configs (owner). Build-time fit below is the initial state
  // (and all a REMOTE player's static hand ever shows).
  g.userData.seat3p = [g.position.x, g.position.y, g.position.z];
  const fit = weaponFit(id); // per-gun 3P seat correction (F4/F8 Guns)
  if (fit.p3) g.position.add(new THREE.Vector3(fit.p3[0], fit.p3[1], fit.p3[2]));
  if (fit.p3r) g.rotation.set(fit.p3r[0], (fit.p3rot ?? 0) + fit.p3r[1], fit.p3r[2]);
  else if (fit.p3rot) g.rotation.y += fit.p3rot;
  return g;
}

// Owner-modelled tool GLBs (2026-07-11): hammer + all axe/pickaxe tiers. Shared by the FP viewmodel and
// the 3P hand (player.ts) -- lives HERE (not viewmodel.ts) because player.ts importing viewmodel would
// cycle (viewmodel imports getPlayerSkin from player). Raw exports lie along +X like the guns, so stand
// them up (+X -> +Y, head at the top) and raise the centre so the hand grips the lower third.
const TOOL_GLB: Record<string, string> = {
  hammer: 'hammer.glb',
  wood_axe: 'wooden_axe.glb', stone_axe: 'stone_axe.glb', metal_axe: 'metal_axe.glb',
  wood_pickaxe: 'wooden_pickaxe.glb', stone_pickaxe: 'stone_pickaxe.glb', metal_pickaxe: 'metal_pickaxe.glb',
};
for (const f of Object.values(TOOL_GLB)) preloadGLB('/models/' + f);
// F8 fit for NON-weapon handhelds (owner: F8 tunes EVERYTHING): fp/rot adjust the first-person seat,
// p3/p3r the third-person one -- applied to the wrapper so the builder's base pose stays intact.
export function applyHeldFit(g: THREE.Group, id: string, threeP: boolean): void {
  const fit = weaponFit(id);
  if (fit.scale) g.scale.multiplyScalar(fit.scale);
  if (fit.scl) g.scale.multiply(new THREE.Vector3(fit.scl[0] || 1, fit.scl[1] || 1, fit.scl[2] || 1));
  if (!threeP) {
    if (fit.fp) g.position.set(fit.fp[0], fit.fp[1], fit.fp[2]);
    if (fit.rot) g.rotation.set(fit.rot[0], fit.rot[1], fit.rot[2]);
  } else {
    g.userData.seat3p = [g.position.x, g.position.y, g.position.z]; // player.ts recomputes rest/aim absolutes from here
    if (fit.p3) g.position.set(g.position.x + fit.p3[0], g.position.y + fit.p3[1], g.position.z + fit.p3[2]);
    if (fit.p3r) g.rotation.set(fit.p3r[0], fit.p3r[1], fit.p3r[2]);
  }
}
export function buildToolGlb(id: string, fallback: () => THREE.Group): THREE.Group {
  const file = TOOL_GLB[id];
  const ready = file ? cloneGLBIfReady('/models/' + file) : null;
  if (!ready || !file) return fallback();
  const g = new THREE.Group();
  const fitted = fitGLBHeld(ready, file, 0.55);
  fitted.rotation.z = -Math.PI / 2; // raw head sits at -X: -X -> +Y puts the head UP, handle in the fist
  fitted.position.y = 0.12;         // grip at the lower third of the handle
  g.add(fitted);
  return g;
}

// ===================== TORCH ON/OFF (owner 2026-07-11) =====================
// The torch is OFF by default; right-click toggles it. main owns the transitions (lit + the eased lit01:
// 2s fade-in to full flame, 1s snuff), both held-model builders read it, and the shared particle rig below
// renders programmatic FIRE (additive radial-gradient sprites cycling up from the head, shrinking + fading)
// and SMOKE (soft gray sprites, slower + growing) -- smoke lingers ~4s after a snuff.
export const torchState = { lit: false, lit01: 0 };
preloadGLB('/models/torch_on.glb');
preloadGLB('/models/torch_off.glb');

function radialTex(inner: string, mid: string): THREE.CanvasTexture {
  const size = 64, cv = document.createElement('canvas'); cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, inner); g.addColorStop(0.45, mid); g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
let fireTex: THREE.CanvasTexture | null = null, smokeTex: THREE.CanvasTexture | null = null;

// Particle rig parented at the torch HEAD. userData.tick(dt, lit01) drives it; safe to tick at 0 (idle).
export function makeTorchFx(): THREE.Group {
  fireTex = fireTex ?? radialTex('rgba(255,240,180,1)', 'rgba(255,120,20,0.55)');
  smokeTex = smokeTex ?? radialTex('rgba(90,88,84,0.65)', 'rgba(90,88,84,0.3)');
  const g = new THREE.Group();
  g.name = 'torchFx';
  const fire: THREE.Sprite[] = [], smoke: THREE.Sprite[] = [];
  for (let i = 0; i < 7; i++) {
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: fireTex, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
    sp.renderOrder = 3; g.add(sp); fire.push(sp);
  }
  for (let i = 0; i < 4; i++) {
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: smokeTex, transparent: true, depthWrite: false }));
    sp.renderOrder = 3; g.add(sp); smoke.push(sp);
  }
  let t = 0, linger = 0, prevLit = 0;
  g.userData.tick = (dt: number, lit01: number): void => {
    t += dt;
    if (lit01 < prevLit && prevLit > 0.5) linger = 4; // just snuffed: smoke hangs around
    else if (lit01 <= 0.05) linger = Math.max(0, linger - dt);
    prevLit = lit01;
    const smokeAmt = Math.max(lit01 * 0.7, lit01 <= 0.05 ? (linger > 0 ? 0.8 * Math.min(1, linger / 4) : 0) : 0);
    fire.forEach((sp, i) => {
      const k = ((t * 1.9 + i / 7) % 1); // 0..1 life, staggered
      sp.position.set(Math.sin(i * 2.1 + t * 7) * 0.02, k * 0.22, Math.cos(i * 1.7 + t * 6) * 0.02);
      sp.scale.setScalar(Math.max(0.001, (0.16 - k * 0.12) * lit01));
      (sp.material as THREE.SpriteMaterial).opacity = Math.max(0, (1 - k) * 0.9 * lit01);
    });
    smoke.forEach((sp, i) => {
      const k = ((t * 0.55 + i / 4) % 1);
      sp.position.set(Math.sin(i * 3.1 + t * 1.3) * 0.05, 0.1 + k * 0.6, Math.cos(i * 2.3 + t) * 0.05);
      sp.scale.setScalar(Math.max(0.001, (0.08 + k * 0.22) * Math.max(smokeAmt, 0.001)));
      (sp.material as THREE.SpriteMaterial).opacity = Math.max(0, (1 - k) * 0.35 * smokeAmt);
    });
  };
  return g;
}

// Fitted torch GLB (on/off by torchState.lit) with the fire/smoke rig at the head; null until the GLB is
// warm (callers keep their procedural stick as the fallback).
export function buildTorchGlb(): THREE.Group | null {
  const file = torchState.lit ? 'torch_on.glb' : 'torch_off.glb';
  const ready = cloneGLBIfReady('/models/' + file);
  if (!ready) return null;
  const g = new THREE.Group();
  const fitted = fitGLBHeld(ready, file, 0.5);
  fitted.rotation.z = -Math.PI / 2; // raw along +X, head at -X (tool convention): stand it up, head at top
  fitted.position.y = 0.1;
  g.add(fitted);
  const box = new THREE.Box3(); box.makeEmpty();
  eachVertexLocal(g, (v) => box.expandByPoint(v));
  const fx = makeTorchFx();
  fx.position.set(0, (box.isEmpty() ? 0.4 : box.max.y) - 0.03, 0);
  g.add(fx);
  return g;
}

// The AK is the legacy weapon (not a WeaponId), but it shares this scrappy material vocabulary. FP frame (~y0.5, -Z).
export function buildAkFp(): THREE.Group {
  const g = new THREE.Group();
  box(g, 0.072, 0.11, 0.34, 0, 0.5, -0.26, STEEL(0x2f3237));       // dark receiver
  for (const z of [-0.36, -0.22, -0.1]) rivet(g, 0.037, 0.52, z);
  box(g, 0.058, 0.058, 0.22, 0, 0.5, -0.02, WOOD(0x7a4a1e));       // rear upper handguard (wood)
  box(g, 0.056, 0.062, 0.17, 0, 0.5, -0.46, WOOD(0x7a4a1e));       // fore handguard (wood)
  rod(g, 0.016, 0.44, 0, 0.53, -0.64, GUNMETAL(), 'z', 14);        // barrel
  rod(g, 0.009, 0.22, 0, 0.565, -0.52, GUNMETAL(), 'z', 10);       // gas tube
  for (let i = 0; i < 3; i++) box(g, 0.05, 0.085, 0.075, 0, 0.4 - i * 0.06, -0.24 + i * 0.035, GUNMETAL(), 0.22 + i * 0.14); // banana mag (curved)
  box(g, 0.06, 0.09, 0.26, 0, 0.5, 0.08, WOOD(0x7a4a1e), 0.03);    // wood stock
  grip(g, 0.05, 0.12, 0.05, 0, 0.42, -0.14, -0.32);
  triggerGuard(g, 0, 0.44, -0.2);
  box(g, 0.03, 0.07, 0.04, 0, 0.575, -0.52, GUNMETAL());           // front sight block
  rod(g, 0.024, 0.07, 0, 0.53, -0.88, GUNMETAL(), 'z', 12);        // slanted muzzle brake
  return g;
}
export function buildAk3p(): THREE.Group {
  const g = buildAkFp();
  g.position.y -= 0.5;
  g.userData.seat3p = [g.position.x, g.position.y, g.position.z];
  const fit = weaponFit('ak');
  if (fit.p3) g.position.add(new THREE.Vector3(fit.p3[0], fit.p3[1], fit.p3[2]));
  if (fit.p3r) g.rotation.set(fit.p3r[0], (fit.p3rot ?? 0) + fit.p3r[1], fit.p3r[2]);
  else if (fit.p3rot) g.rotation.y += fit.p3rot;
  return g;
}
